/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    inject,
    Injectable,
    OnDestroy,
    signal,
    WritableSignal,
} from "@angular/core";
import { Router } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import {
    DefaultAndroidSystemBrowserOptions,
    DefaultiOSSystemBrowserOptions,
    InAppBrowser,
} from "@capacitor/inappbrowser";
import {
    ActionPerformed,
    PushNotifications,
    Token,
} from "@capacitor/push-notifications";
import { Badge } from "@capawesome/capacitor-badge";
import { FcmService, NotificationService, UserDto } from "@malirang/api-client";
import { MemberRole } from "@malirang/common";
import { EventSourcePolyfill } from "event-source-polyfill";
import { BehaviorSubject } from "rxjs";
import { environment } from "../../env/env";

export interface ConnectEventSourceOptions {
    userId: string;
    token: string;
}

@Injectable({ providedIn: "root" })
export class NotificationStore implements OnDestroy {
    private readonly notificationService = inject(NotificationService);
    private readonly fcmService = inject(FcmService);
    private readonly router = inject(Router);

    private readonly eventSourceUrl: string = `${environment.baseUrl}/api/v1/notification/send/parent`;
    private readonly retryInterval: number = 3000;

    private userId: WritableSignal<string> = signal<string>("");
    private token: WritableSignal<string> = signal<string>("");
    private eventSource: EventSourcePolyfill | null = null;

    public count$ = new BehaviorSubject(0);

    ngOnDestroy(): void {
        this.closeSSE();
    }

    /**
     * @name fetchState
     * @description get notifications
     * @returns {void}
     */
    public fetchState(): void {
        this.notificationService
            .notificationControllerCountV1({
                pageNo: 1,
                pageSize: 9999,
                memberRole: environment.role as keyof typeof MemberRole,
            })
            .subscribe({
                next: async (count: number) => {
                    this.count$.next(count);
                    await Badge.set({
                        count,
                    });
                },
            });
    }

    /**
     * @name clear
     * @description reset notifications
     * @returns {void}
     */
    public async clear(): Promise<void> {
        await this.clearBadge();
        this.count$.next(0);
    }

    /**
     * @name clearBadge
     * @description clear native app badge
     * @returns {void}
     */
    public async clearBadge(): Promise<void> {
        await Badge.clear();
    }

    /**
     * @name connect
     * @description connect to SSE for real-time notifications
     * @param {ConnectEventSourceOptions} connectEventSourceOptions
     * @returns {void}
     */
    public async connectSSE(
        connectEventSourceOptions: ConnectEventSourceOptions,
    ): Promise<void> {
        const { userId, token } = connectEventSourceOptions;
        if (!userId || !token) return;
        if (this.eventSource) return;

        this.userId.set(userId);
        this.token.set(token);
        this.eventSource = new EventSourcePolyfill(this.eventSourceUrl, {
            headers: {
                Authorization: `Bearer ${this.token()}`,
            },
        });

        // connect success
        this.eventSource.onopen = () => {
            this.fetchState();
            if (this.eventSource) {
                console.debug("SSE connection success");
            }
        };

        this.eventSource.onmessage = async (event: any) => {
            console.debug("SSE Message Received:", event);
            if (event?.data === userId) {
                console.debug("SSE Message for User:", userId);
                this.fetchState();
            }
        };

        this.eventSource.onerror = (err) => {
            this.closeSSE();
            this.eventSource = null;
            setTimeout(async () => {
                await this.connectSSE({
                    userId: this.userId(),
                    token: this.token(),
                });
            }, this.retryInterval || 3000);
        };
    }

    /**
     * @name closeSSE
     * @description close sse
     * @returns {void}
     */
    public closeSSE(): void {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
    }

    /**
     * @name saveToken
     * @description update FCM token for the user
     *  @param {UserDto | undefined | null} user
     * @returns {Promise<void>}
     */
    public async updateFCMToken(
        user: UserDto | undefined | null,
    ): Promise<void> {
        setTimeout(async () => {
            if (!user) return;
            if (!Capacitor.isNativePlatform()) return;
            try {
                const { receive } =
                    await PushNotifications.requestPermissions();
                if (receive !== "granted") return;
                await PushNotifications.register();
                await PushNotifications.addListener(
                    "registration",
                    async (token: Token) => {
                        if (!user?.fcmTokens?.includes(token.value)) {
                            try {
                                this.fcmService
                                    .fcmControllerUpsertFcmToken({
                                        token: token.value,
                                        memberRole:
                                            environment.role as keyof typeof MemberRole,
                                    })
                                    .subscribe({
                                        next: () => {},
                                        error: (error) => {
                                            console.error(
                                                "Error updating Firebase token",
                                                error,
                                            );
                                        },
                                    });
                            } catch (err) {
                                console.error(
                                    "Error updating Firebase token",
                                    err,
                                );
                            }
                        }
                    },
                );
                // Method called when a push notification is received
                await PushNotifications.addListener(
                    "registrationError",
                    (error: any) => {
                        console.error("Registration error :", error);
                    },
                );

                // Method called when tapping on a notification
                PushNotifications.addListener(
                    "pushNotificationActionPerformed",
                    async (notification: ActionPerformed) => {
                        // Navigate to the URL provided in the notification data
                        try {
                            const data = notification?.notification?.data;
                            console.debug(
                                "Notification clicked with data:",
                                data,
                            );
                            if (data && data.url) {
                                try {
                                    setTimeout(async () => {
                                        if (
                                            data?.url?.includes(
                                                "parents.malirang.com",
                                            )
                                        ) {
                                            this.router.navigateByUrl(data.url);
                                        } else {
                                            await InAppBrowser.openInSystemBrowser(
                                                {
                                                    url: data.url,
                                                    options: {
                                                        android:
                                                            DefaultAndroidSystemBrowserOptions,
                                                        iOS: DefaultiOSSystemBrowserOptions,
                                                    },
                                                },
                                            );
                                        }
                                    }, 300);
                                } catch (e) {
                                    // 인앱 브라우저로 열기
                                    if (Capacitor.isNativePlatform()) {
                                        await InAppBrowser.openInSystemBrowser({
                                            url: data.url,
                                            options: {
                                                android:
                                                    DefaultAndroidSystemBrowserOptions,
                                                iOS: DefaultiOSSystemBrowserOptions,
                                            },
                                        });
                                    } else {
                                        window.open(data.url, "_blank");
                                    }
                                }
                            }
                        } catch (error) {
                            console.error(
                                "Error handling notification click:",
                                error,
                            );
                        }
                    },
                );
            } catch (error) {
                console.error(
                    "Error requesting push notification permissions",
                    error,
                );
            }
        }, 3000);
    }
}
